/**
 * Lighthouse Inventory — every discovered agent, publisher, LLM endpoint,
 * MCP server, and credential-access event. 5 tabs, URL-persisted state,
 * graceful fallbacks when P2 endpoints aren't live yet.
 */
import { useMemo } from "react";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { OwnerPill } from "@/components/owner-pill";
import { useUrlState } from "@/hooks/useUrlState";
import {
  useLighthouseAgents,
  useLighthouseAttestations,
  useLighthouseMcpServers,
  useLighthouseInventorySummary,
  useLighthouseLlmEndpoints,
  useLighthouseCredentialAccess,
} from "@/hooks/security/useLighthouseData";
import { formatDistanceToNow } from "date-fns";
import type { LighthouseAgent } from "@/types/lighthouse";
import type {
  McpServerDTO,
  LlmEndpointDTO,
  CredentialAccessDTO,
} from "@/types/lighthouseApi";
import { Plug, Cloud, KeyRound, Building2, Bot } from "lucide-react";

type TabKey = "agents" | "publishers" | "llm" | "mcp" | "credentials";

export default function LhInventoryPage() {
  return (
    <ConnectorGate connectorId="lighthouse">
      <Inner />
    </ConnectorGate>
  );
}

function Inner() {
  const agentsQ = useLighthouseAgents();
  const attQ = useLighthouseAttestations();
  const mcpQ = useLighthouseMcpServers();
  const summaryQ = useLighthouseInventorySummary();
  const llmQ = useLighthouseLlmEndpoints();
  const credQ = useLighthouseCredentialAccess();

  const [tab, setTab] = useUrlState<TabKey>("tab", "agents");
  const [q, setQ] = useUrlState<string>("q", "");
  const [classification, setClassification] = useUrlState<
    "all" | "sanctioned" | "shadow" | "unknown" | "retired"
  >("class", "all");
  const [hasOwner, setHasOwner] = useUrlState<"all" | "yes" | "no">("owner", "all");

  const agents = agentsQ.data ?? [];
  const att = attQ.data ?? [];
  const mcp = mcpQ.data ?? [];
  const llm = llmQ.data ?? [];
  const cred = credQ.data ?? [];
  const summary = summaryQ.data;

  const publishers = useMemo(() => {
    const m = new Map<string, { count: number; sanctioned: number; risky: number }>();
    agents.forEach((a) => {
      const entry = m.get(a.vendor) ?? { count: 0, sanctioned: 0, risky: 0 };
      entry.count += 1;
      if (a.classification === "sanctioned") entry.sanctioned += 1;
      if (a.riskScore >= 70) entry.risky += 1;
      m.set(a.vendor, entry);
    });
    return Array.from(m.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [agents]);

  // Prefer summary endpoint, fall back to client-side aggregation.
  const counts = {
    agents: summary?.agentCount ?? agents.length,
    publishers: summary?.publisherCount ?? publishers.length,
    unattributed:
      summary?.unattributedCount ??
      agents.filter((a) => !a.owner).length,
    llm: summary?.llmEndpointCount ?? llm.length,
    mcp: summary?.mcpServerCount ?? mcp.length,
    credentials: summary?.credentialAccessCount ?? cred.length,
  };

  return (
    <SurfaceShell
      overline="AI Governance · Inventory"
      title="Everything we know about"
      summary={`${counts.agents} agents from ${counts.publishers} publishers · ${counts.unattributed} awaiting owner · ${counts.llm} LLM endpoints · ${counts.mcp} MCP servers in use.`}
      error={agentsQ.isError ? agentsQ.error : undefined}
      onRetry={agentsQ.isError ? () => agentsQ.refetch() : undefined}
      kpis={
        <>
          <StatTile
            label="Agents"
            value={counts.agents.toLocaleString()}
            loading={agentsQ.isLoading || agentsQ.isError}
            hint={`${att.length} attestations on file`}
          />
          <StatTile
            label="Publishers"
            value={counts.publishers}
            loading={agentsQ.isLoading || agentsQ.isError}
          />
          <StatTile
            label="Awaiting owner"
            value={counts.unattributed}
            loading={agentsQ.isLoading || summaryQ.isLoading}
            tone={counts.unattributed > 0 ? "warning" : "success"}
            hint="No IdP binding yet"
          />
          <StatTile
            label="LLM endpoints"
            value={counts.llm}
            loading={llmQ.isLoading}
            hint={`${counts.mcp} MCP servers`}
          />
        </>
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="agents">
            <Bot className="h-3.5 w-3.5 mr-1.5" /> Agents · {counts.agents}
          </TabsTrigger>
          <TabsTrigger value="publishers">
            <Building2 className="h-3.5 w-3.5 mr-1.5" /> Publishers · {counts.publishers}
          </TabsTrigger>
          <TabsTrigger value="llm">
            <Cloud className="h-3.5 w-3.5 mr-1.5" /> LLM Endpoints · {counts.llm}
          </TabsTrigger>
          <TabsTrigger value="mcp">
            <Plug className="h-3.5 w-3.5 mr-1.5" /> MCP Servers · {counts.mcp}
          </TabsTrigger>
          <TabsTrigger value="credentials">
            <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Credentials · {counts.credentials}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="mt-4">
          <AgentsTable
            agents={agents}
            loading={agentsQ.isLoading}
            error={agentsQ.isError ? agentsQ.error : undefined}
            onRetry={() => agentsQ.refetch()}
            q={q}
            setQ={setQ}
            classification={classification}
            setClassification={setClassification}
            hasOwner={hasOwner}
            setHasOwner={setHasOwner}
          />
        </TabsContent>

        <TabsContent value="publishers" className="mt-4">
          <PublishersTable rows={publishers} loading={agentsQ.isLoading} />
        </TabsContent>

        <TabsContent value="llm" className="mt-4">
          <LlmTable
            rows={llm}
            loading={llmQ.isLoading}
            error={llmQ.isError ? llmQ.error : undefined}
            onRetry={() => llmQ.refetch()}
          />
        </TabsContent>

        <TabsContent value="mcp" className="mt-4">
          <McpTable
            rows={mcp}
            loading={mcpQ.isLoading}
            error={mcpQ.isError ? mcpQ.error : undefined}
            onRetry={() => mcpQ.refetch()}
          />
        </TabsContent>

        <TabsContent value="credentials" className="mt-4">
          <CredentialAccessTable
            rows={cred}
            loading={credQ.isLoading}
            error={credQ.isError ? credQ.error : undefined}
            onRetry={() => credQ.refetch()}
          />
        </TabsContent>
      </Tabs>
    </SurfaceShell>
  );
}

// ---------- Agents ----------

interface AgentsTableProps {
  agents: LighthouseAgent[];
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
  q: string;
  setQ: (v: string) => void;
  classification: "all" | "sanctioned" | "shadow" | "unknown" | "retired";
  setClassification: (
    v: "all" | "sanctioned" | "shadow" | "unknown" | "retired",
  ) => void;
  hasOwner: "all" | "yes" | "no";
  setHasOwner: (v: "all" | "yes" | "no") => void;
}

function AgentsTable({
  agents,
  loading,
  error,
  onRetry,
  q,
  setQ,
  classification,
  setClassification,
  hasOwner,
  setHasOwner,
}: AgentsTableProps) {
  const filtered = useMemo(() => {
    return agents.filter((a) => {
      if (classification !== "all" && a.classification !== classification) return false;
      if (hasOwner === "yes" && !a.owner) return false;
      if (hasOwner === "no" && a.owner) return false;
      if (q && !`${a.name} ${a.vendor}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [agents, classification, hasOwner, q]);

  return (
    <Panel
      title={`Agents · ${filtered.length}`}
      loading={loading}
      isError={!!error}
      error={error}
      onRetry={onRetry}
      empty={!loading && !error && filtered.length === 0}
      emptyTitle={agents.length === 0 ? "No agents discovered yet" : "No agents match filters"}
      emptyDescription={
        agents.length === 0
          ? "Once Lighthouse-aware sensors check in, discovered agents will appear here."
          : "Try clearing the search or filter chips."
      }
      skeleton="table"
      actions={
        <div className="flex items-center gap-2">
          <FilterChip
            label="Classification"
            value={classification}
            options={[
              { value: "all", label: "All" },
              { value: "sanctioned", label: "Sanctioned" },
              { value: "shadow", label: "Shadow" },
              { value: "unknown", label: "Unknown" },
              { value: "retired", label: "Retired" },
            ]}
            onChange={(v) => setClassification(v as typeof classification)}
          />
          <FilterChip
            label="Owner"
            value={hasOwner}
            options={[
              { value: "all", label: "Any" },
              { value: "yes", label: "Has owner" },
              { value: "no", label: "Awaiting" },
            ]}
            onChange={(v) => setHasOwner(v as typeof hasOwner)}
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filter…"
            className="h-8 w-[220px] text-xs"
          />
        </div>
      }
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {["Agent", "Publisher", "Class", "Owner", "Risk", "Last seen"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2 text-foreground font-medium truncate max-w-[260px]">
                  {a.name}
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">
                  {a.vendor}
                </td>
                <td className="px-3 py-2">
                  <ClassChip classification={a.classification} />
                </td>
                <td className="px-3 py-2">
                  <OwnerPill consoleUser={a.owner} size="sm" />
                </td>
                <td className="px-3 py-2 tabular-nums">
                  <RiskChip score={a.riskScore} />
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatDistanceToNow(new Date(a.lastSeen), { addSuffix: true })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ClassChip({ classification }: { classification: LighthouseAgent["classification"] }) {
  const map: Record<LighthouseAgent["classification"], string> = {
    sanctioned: "bg-success-muted text-success-muted-foreground border-success-border",
    shadow: "bg-destructive/10 text-destructive border-destructive/30",
    unknown: "bg-muted text-muted-foreground border-border",
    retired: "bg-muted/40 text-muted-foreground border-border",
    quarantined: "bg-destructive/20 text-destructive border-destructive/40",
  };
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border capitalize ${map[classification]}`}
    >
      {classification}
    </span>
  );
}

function RiskChip({ score }: { score: number }) {
  const cls =
    score >= 70
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : score >= 40
        ? "bg-warning/15 text-warning-muted-foreground border-warning-border"
        : "bg-success-muted text-success-muted-foreground border-success-border";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] border tabular-nums ${cls}`}>
      {score}
    </span>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
      <span className="uppercase tracking-wider">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------- Publishers ----------

function PublishersTable({
  rows,
  loading,
}: {
  rows: { name: string; count: number; sanctioned: number; risky: number }[];
  loading: boolean;
}) {
  return (
    <Panel
      title={`Publishers · ${rows.length}`}
      loading={loading}
      empty={!loading && rows.length === 0}
      emptyTitle="No publishers yet"
      emptyDescription="Publishers will appear once agents are discovered."
      skeleton="list"
    >
      <ul className="divide-y divide-border -mx-4 -my-4">
        {rows.map((p) => (
          <li
            key={p.name}
            className="px-4 py-2.5 flex items-center justify-between gap-3"
          >
            <div className="min-w-0">
              <div className="text-sm text-foreground truncate">{p.name}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums">
                {p.sanctioned} sanctioned · {p.risky} high-risk
              </div>
            </div>
            <Badge variant="outline" className="tabular-nums flex-none">
              {p.count} agent{p.count === 1 ? "" : "s"}
            </Badge>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

// ---------- LLM endpoints ----------

function LlmTable({
  rows,
  loading,
  error,
  onRetry,
}: {
  rows: LlmEndpointDTO[];
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
}) {
  return (
    <Panel
      title={`LLM endpoints · ${rows.length}`}
      loading={loading}
      isError={!!error}
      error={error}
      onRetry={onRetry}
      empty={!loading && !error && rows.length === 0}
      emptyTitle="No LLM endpoints observed"
      emptyDescription="Once agents start calling out to model APIs, sanctioned and shadow endpoints will show up here."
      skeleton="table"
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {["Endpoint", "Provider", "Sanctioned", "Calls 24h", "Agents", "Last seen"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id ?? `${r.baseUrl}-${i}`}
                className="border-b border-border/60 hover:bg-muted/40"
              >
                <td className="px-3 py-2 text-foreground font-medium truncate max-w-[260px]">
                  {r.name ?? r.baseUrl ?? "—"}
                  {r.baseUrl && r.name && (
                    <div className="text-[10px] text-muted-foreground font-mono truncate">
                      {r.baseUrl}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.provider ?? "—"}</td>
                <td className="px-3 py-2">
                  {r.sanctioned ? (
                    <Badge
                      variant="outline"
                      className="bg-success-muted text-success-muted-foreground border-success-border"
                    >
                      sanctioned
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-warning/10 text-warning-muted-foreground border-warning-border"
                    >
                      shadow
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 tabular-nums text-foreground">
                  {(r.callsLast24h ?? 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 tabular-nums text-muted-foreground">
                  {r.agentsUsing ?? 0}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.lastSeenAt
                    ? formatDistanceToNow(new Date(r.lastSeenAt), { addSuffix: true })
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---------- MCP servers ----------

function McpTable({
  rows,
  loading,
  error,
  onRetry,
}: {
  rows: McpServerDTO[];
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
}) {
  return (
    <Panel
      title={`MCP servers · ${rows.length}`}
      loading={loading}
      isError={!!error}
      error={error}
      onRetry={onRetry}
      empty={!loading && !error && rows.length === 0}
      emptyTitle="No MCP servers discovered"
      emptyDescription="Discovered MCP servers (including shadow ones) will appear here once agents connect."
      skeleton="table"
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {["Server", "Publisher", "Transport", "Tools", "Agents", "Devices", "Risk"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id ?? r.server_id ?? `mcp-${i}`}
                className="border-b border-border/60 hover:bg-muted/40"
              >
                <td className="px-3 py-2 text-foreground font-medium truncate max-w-[260px]">
                  {r.name ?? r.url ?? "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">
                  {r.publisher ?? "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground capitalize">
                  {r.transport ?? "—"}
                </td>
                <td className="px-3 py-2 tabular-nums">{r.tool_count ?? 0}</td>
                <td className="px-3 py-2 tabular-nums">{r.agent_count ?? 0}</td>
                <td className="px-3 py-2 tabular-nums">{r.device_count ?? 0}</td>
                <td className="px-3 py-2">
                  {typeof r.risk_score === "number" ? (
                    <RiskChip score={r.risk_score} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

// ---------- Credential access ----------

function CredentialAccessTable({
  rows,
  loading,
  error,
  onRetry,
}: {
  rows: CredentialAccessDTO[];
  loading: boolean;
  error?: unknown;
  onRetry: () => void;
}) {
  if (!loading && !error && rows.length === 0) {
    return (
      <EmptyState
        icon={KeyRound}
        title="No credential access observed"
        description="When Lighthouse-aware agents read or modify credential files, the events show up here so you can spot shadow tooling reaching for secrets."
      />
    );
  }
  return (
    <Panel
      title={`Credential access events · ${rows.length}`}
      loading={loading}
      isError={!!error}
      error={error}
      onRetry={onRetry}
      skeleton="table"
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {["Path", "Agent", "Device", "Access", "Last seen"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id ?? `cred-${i}`}
                className="border-b border-border/60 hover:bg-muted/40"
              >
                <td className="px-3 py-2 text-foreground font-mono truncate max-w-[300px]">
                  {r.path ?? "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[180px]">
                  {r.agentName ?? r.agentId ?? "—"}
                </td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-[160px]">
                  {r.deviceName ?? r.deviceId ?? "—"}
                </td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="capitalize">
                    {r.accessKind ?? "—"}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.lastSeenAt
                    ? formatDistanceToNow(new Date(r.lastSeenAt), { addSuffix: true })
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
